import React, { ChangeEvent, useCallback, useEffect, useImperativeHandle, useState, forwardRef, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import styles from '../../entity-editor/EntityEditor.styles';
import PostcodeForm, { PostcodeFormHandle } from '../../postcode-form/PostcodeForm';
import StyledTextField from '../../styled-components/StyledTextField';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { NumericTextField } from '../../numeric-text-field/NumericTextField';
import { LatinTextField } from '../../latin-text-field/LatinTextField';
import { usePurposes } from '../../purpose-autucomplete/usePurposes';
import PurposeAutocomplete from '../../purpose-autucomplete/PurposeAutocomplete';
import AlertService from '../../../services/notification.service';
// import { initialErrorEditState } from '../../entity-editor/entity-editor-blocks/EntityAddressBlock';
import { normalize } from '../../../utils';
import { ClipToolTip } from '../../clip-tooltip/ClipToolTip';

export interface AddressForm {
  street_name: string;
  number: string | number;
  number_add: string | number | null;
  postcode?: string | null;
  city_code_id?: number | null;
  city_id?: number | null;
  id?: number | null;
  municipality_id?: number | null;
  section_id?: number | null;
  unit_id?: number | null;
}

export interface UnitCandidate {
  size: string | number | null;
  built_year: string | number | null;
  price: string | number | null;
  purpose: string | null;
  id?: string | number | null;
  bug_id?: string | number | null;
  bug_url?: string | number | null;
  address: AddressForm;
}

export interface UnitDetails extends UnitCandidate {
  index: number;
  modified: boolean;
}

export const initialErrorEditUnitState = {
  street_name: false,
  number: false,
  number_add: false,
  built_year: false
};

export type ObjectUnitsBlockHandle = {
  reset: () => void;
}

interface ObjectUnitsBlockProps {
  units?: any,
  onChange?: any,
}

const initialEditModeState = {
  isEdit: false,
  index: -1
};
const initialUnitState = {
  size: '',
  built_year: '',
  price: '',
  purpose: '',
  bug_id: '',
  bug_url: '',
  address: {
    street_name: '',
    number: '',
    number_add: '',
    postcode: ''
  }
};

const initialUnitDetails = {
  size: '',
  built_year: '',
  price: '',
  purpose: '',
  bug_id: '',
  bug_url: '',
  address: {
    street_name: '',
    number: '',
    number_add: '',
    postcode: ''
  },
  index: -1,
  id: '',
  modified: false
};

const isDuplicatedAddress = (candidate: UnitCandidate, list: UnitCandidate[], selectedIndex?: number): boolean => {
  return list.some((item: UnitCandidate, index) => {
    return normalize(item.address.street_name) === normalize(candidate.address.street_name)
      && normalize(item.address.number) === normalize(candidate.address.number)
      && normalize(item.address.number_add) === normalize(candidate.address.number_add)
      && (selectedIndex == null || selectedIndex !== index);
  });
};
const hasAnyErrors = (errors: Record<string, boolean>): boolean => {
  return Object.values(errors).some(Boolean);
};

const toStr = (value: unknown) =>
  value === null || value === undefined ? "" : String(value).trim();


const ObjectUnitsBlock = forwardRef<ObjectUnitsBlockHandle, ObjectUnitsBlockProps>(({ units, onChange }, ref) => {
  const dispatch = useDispatch();

  const [editMode, setEditMode] = useState(initialEditModeState);
  const [objectUnit, setObjectUnit] = useState<UnitCandidate>(initialUnitState);
  const [objectUnitsList, setObjectUnitsList] = useState([]);
  const [editedUnitDetails, setEditedUnitDetails] =
    useState<UnitDetails>(initialUnitDetails);
  const [errorUnitFields, setErrorUnitFields] = useState(initialErrorEditUnitState);
  const [check, setCheck] = useState<boolean>(true);
  const [checkAdd, setCheckAdd] = useState<boolean>(true);
  const [unitForDelete, setUnitForDelete] = useState([]);
  const [updateTypes, setUpdateTypes] = useState<Array<string>>([]);
  const postcodeRef = useRef<PostcodeFormHandle | null>(null);

  const { options, loading, error, lastQuery, suggest, clear } = usePurposes();

  const type = 'objectUnit';

  useEffect(() => {
    setObjectUnitsList(units);
  }, [dispatch, units]);

  // useEffect(() => {
  //   const newState = {
  //     filteredList: objectUnitsList,
  //     newStateForDelete: unitForDelete,
  //   };
  //   onChange(updateTypes, newState, true);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [unitForDelete]);

  const isEmpty = (value: unknown): boolean => {
    if (value === null || value === undefined) {
      return true;
    }
    if (typeof value === 'string') return value.trim() === '';
    return false;
  };

  const validateRequiredAddressField = (obj: UnitCandidate) => {
    const streetEmpty = isEmpty(obj.address.street_name);
    const numberEmpty = isEmpty(obj.address.number);
    // setErrorUnitFields(prev => ({ ...prev, number: streetEmpty}));
    setErrorUnitFields(prev => ({
      ...prev,
      street_name: streetEmpty,
      number: numberEmpty
    }));
    const hasError = streetEmpty || numberEmpty;
    return !hasError;
  };
  const isAddUnitDisabled = hasAnyErrors(errorUnitFields)
    || isEmpty(objectUnit.address.street_name)
    || isEmpty(objectUnit.address.number)
  const warningAddressDuplicate = () => {
    return AlertService.warning('This address is already on the list.');
  };
  const warningRequiredFields = () => {
    return  AlertService.warning('To add a unit address, fill in the street and number fields.');
  };

  const handleClearForm = () => {
    /** clear all fields and error statuses **/
    setEditedUnitDetails(initialUnitDetails);
    setObjectUnit(initialUnitState);
    setEditMode(initialEditModeState);
    setErrorUnitFields(initialErrorEditUnitState);
    setUnitForDelete([]);
    setObjectUnitsList([]);
    setUpdateTypes([]);
    setCheck(false);
    setCheckAdd(false);
  };

  const handleCancelMouse = () => {
    setCheck(false);
    setCheckAdd(false);
    setErrorUnitFields(initialErrorEditUnitState);
  };

  const reset = useCallback(() => {
    handleClearForm();
    postcodeRef.current?.reset();
  }, []);
  useImperativeHandle(ref, () => ({ reset }), [reset]);

  const handleChange = useCallback(
    (name: string,
     key: string,
     event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string
    ) => {
      const value = typeof event === 'string' ? event : event.target.value;
      setUpdateTypes(prev => Array.from(new Set([...prev, name, 'units'])));

      if (name === 'units_addresses') {
        setObjectUnit((prev) => ({
          ...prev,
          address: {
            ...prev.address,
            [key]: value
          }
        }));
      } else {
        setObjectUnit((prev) => ({
          ...prev,
          [key]: value
        }));
      }
    },
    [setUpdateTypes, setObjectUnit]);

  const handleChangeItem = useCallback((
    name: string,
    fieldName: string,
    selectedIndex: number,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string
  ) => {
    const value = typeof event === 'string' ? event : event.target.value;
    setUpdateTypes(prev => Array.from(new Set([...prev, name, 'units'])));
    setEditedUnitDetails(prev => {
      const base = {
        ...prev,
        index: selectedIndex,
        modified: true
      };
      if (name === 'units_addresses') {
        return {
          ...base,
          address: {
            ...prev.address,
            [fieldName]: value
          }
        };
      }
      return {
        ...base,
        [fieldName]: value
      };
    });
  }, [setUpdateTypes, setEditedUnitDetails]);

  const handleDeleteItem = (selectedIndex: number) => {
    const newStateForDelete = [];
    const editMode = true;
    objectUnitsList.map((item: any, index) => {
      if (index === selectedIndex && item.id) {
        // @ts-ignore
        return newStateForDelete.push(item);
      } else {
        return item;
      }
    });
    const filteredList = objectUnitsList.filter(
      (_item: any, index) => index !== selectedIndex
    );

    const newState = {
      filteredList,
      newStateForDelete
    };

    setObjectUnitsList(filteredList);
    setUnitForDelete([...unitForDelete, ...newStateForDelete]);
    onChange(updateTypes, newState, editMode);
  };

  const handleSetEditMode = (type: string, selectedIndex: number) => {
    /** managing open/close state for ip item change, save changes by click in ip list **/
    if (type === 'open') {
      setErrorUnitFields(initialErrorEditUnitState);
      setCheck(true);
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
      objectUnitsList.forEach((item: any, index) => {
        if (index === selectedIndex) {
          setEditedUnitDetails({
            ...item,
            index: selectedIndex,
            id: item.id || null,
            size: item.size,
            built_year: item.built_year,
            price: item.price,
            purpose: item.purpose,
            bug_id: item.bug_id,
            bug_url: item.bug_url,
            address: item.address
          });
        }
      });
    }
    if (type === 'close') {
      setErrorUnitFields(initialErrorEditUnitState);
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
    }
    if (type === 'edit') {
      if (isDuplicatedAddress(editedUnitDetails, objectUnitsList, selectedIndex)) {
        return warningAddressDuplicate();
      }
      const newState = objectUnitsList.map((item: any, index) => {
        if (index === editedUnitDetails.index && index === selectedIndex) {
          const currentPurpose: string = editedUnitDetails.purpose ?? '';
          return {
            id: editedUnitDetails.id || '',
            size: editedUnitDetails.size || '',
            built_year: editedUnitDetails.built_year || '',
            price: editedUnitDetails.price || '',
            // purpose: editedUnitDetails.purpose || "",
            purpose: currentPurpose,
            bug_id: editedUnitDetails.bug_id || '',
            bug_url: editedUnitDetails.bug_url || '',
            address: editedUnitDetails.address || ''
          };
        } else {
          return item;
        }
      });
      // @ts-ignore
      const uniqueTypes = [...new Set(updateTypes)];
      // @ts-ignore
      setObjectUnitsList(newState);
      onChange(uniqueTypes, newState);
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
      setEditedUnitDetails(editedUnitDetails);
      setErrorUnitFields(initialErrorEditUnitState);
    }
  };

  const addNewUnit = () => {
    const canAdd = validateRequiredAddressField(objectUnit);
    if (!canAdd) {
      warningRequiredFields();
      return;
    }
    if(hasAnyErrors(errorUnitFields)) {
      return;
    }
    if (isDuplicatedAddress(objectUnit, objectUnitsList)) {
      warningAddressDuplicate();
      return;
    }
    const newState = [
      ...objectUnitsList,
      {
        size: objectUnit.size,
        built_year: objectUnit.built_year,
        price: objectUnit.price,
        purpose: objectUnit.purpose,
        bug_id: objectUnit.bug_id,
        bug_url: objectUnit.bug_url,
        address: objectUnit.address
      }
    ];
    // @ts-ignore
    setObjectUnitsList(newState);
    onChange(['units'], newState);
    handleClearForm();
  };

  const getUnitName = (item: any): string => {
    const parts: string[] = [];
    const postcode = toStr(item?.address?.postcode);
    const street = toStr(item?.address?.street_name);
    const number = toStr(item?.address?.number);
    const numberAdd = toStr(item?.address?.number_add);
    const purpose = toStr(item?.purpose);
    const builtYear = toStr(item?.built_year);
    const size = toStr(item?.size);

    if (postcode) parts.push(postcode);
    if (street) parts.push(street);
    const numberFull = [number, numberAdd].filter(Boolean).join(" ");
    if (numberFull) parts.push(numberFull);
    if (purpose) parts.push(`purpose: ${purpose}`);
    if (builtYear) parts.push(`built year: ${builtYear}`);
    if (size) parts.push(`size: ${size} m2`);

    return parts.join(", ");

  };

  return (
    <Accordion sx={{ mb: '1rem' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography component="legend" sx={styles.textField}>
          Units
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" sx={styles.textField}>
          Unit address
        </Typography>
        <PostcodeForm
          ref={postcodeRef}
          type={type}
          selectAddress={setObjectUnit}
          address={objectUnit}
        />
        <Box sx={styles.fieldHolder}>
          <Box sx={styles.fieldBlock}>
            <LatinTextField
              fullWidth
              label="Street"
              name="street_name"
              type="text"
              errorFields={errorUnitFields.street_name}
              setErrorFields={setErrorUnitFields}
              required
              check={checkAdd}
              margin="dense"
              onChange={(e) => handleChange('units_addresses', 'street_name', e)}
              value={objectUnit.address.street_name}
            />
            <NumericTextField
              fullWidth
              label="Number"
              type="text"
              name="number"
              minValue={1}
              required
              errorFields={errorUnitFields.number}
              setErrorFields={setErrorUnitFields}
              check={checkAdd}
              margin="dense"
              value={objectUnit.address.number}
              onChange={(e) => handleChange('units_addresses', 'number', e)}
            />
          </Box>
          <Box sx={styles.fieldBlock}>
            <StyledTextField
              fullWidth
              label="Number add"
              margin="dense"
              onChange={(e) => handleChange('units_addresses', 'number_add', e)}
              value={objectUnit.address.number_add}
            />
            <StyledTextField
              fullWidth
              label="Postcode"
              margin="dense"
              onChange={(e) => handleChange('units_addresses', 'postcode', e)}
              value={objectUnit.address.postcode}
            />
          </Box>
        </Box>
        <Divider sx={{ marginTop: '15px', marginBottom: '15px' }} />
        <Typography variant="body2" sx={styles.textField}>
          General units fields
        </Typography>
        <Box sx={styles.fieldHolder}>
          <Box sx={styles.fieldBlock}>
            <NumericTextField
              fullWidth
              label="Size"
              name="general_units_fields_size"
              margin="dense"
              onChange={(e) => handleChange('units', 'size', e)}
              value={objectUnit.size}
            />
            <NumericTextField
              fullWidth
              label="Built year"
              margin="dense"
              name="built_year"
              maxLength={4}
              minValue={1200}
              errorFields={errorUnitFields.built_year}
              setErrorFields={setErrorUnitFields}
              maxValue={new Date().getFullYear()}
              onChange={(e) => handleChange('units', 'built_year', e)}
              value={objectUnit.built_year}
            />
            <NumericTextField
              fullWidth
              label="Price"
              margin="dense"
              name="general_units_fields_price"
              onChange={(e) => handleChange('units_price', 'price', e)}
              value={objectUnit.price}
            />
          </Box>
          <Box sx={styles.fieldBlock}>
            <PurposeAutocomplete
              value={objectUnit.purpose ?? ''}
              options={options}
              loading={loading}
              error={error}
              freeSolo={false}
              label="Purpose"
              disabled={false}
              onInputChange={(newInput) => {
                handleChange('units', 'purpose', newInput);
                const q = newInput.trim();
                if (q.length < 3) {
                  clear();
                  return;
                }
                if (q !== lastQuery) {
                  suggest(q);
                }
              }}
              onValueChange={(selected) => {
                handleChange('units', 'purpose', selected);
              }}
              onInvalidInput={(message) => AlertService.warning(message)}
            />
            <StyledTextField
              fullWidth
              label="BUG ID"
              margin="dense"
              onChange={(e) => handleChange('units', 'bug_id', e)}
              value={objectUnit.bug_id}
              disabled
            />
            <StyledTextField
              fullWidth
              label="BUG URL"
              margin="dense"
              onChange={(e) => handleChange('units', 'bug_url', e)}
              value={objectUnit.bug_url}
              disabled
            />
          </Box>
        </Box>
        <Button
          sx={styles.button}
          onClick={addNewUnit}
          disabled={isAddUnitDisabled}
        >
          Add new unit
        </Button>
        <Divider sx={{ marginTop: '15px', marginBottom: '15px' }} />
        {objectUnitsList?.length > 0 &&
          objectUnitsList.map(
            (
              item: {
                address: {
                  street_name: string;
                  number: string;
                  number_add: string;
                  postcode: string;
                };
                size: string;
                built_year: string;
                price: string;
                purpose: string;
                bug_id: string;
                bug_url: string;
              },
              index
            ) => {
              return editMode.isEdit && editMode.index === index ? (
                <Box sx={styles.editHolder} key={index}>
                  <Typography variant="body2" sx={styles.textField}>
                    Edit current unit
                  </Typography>
                  <Box sx={styles.editFieldsBlockHolder}>
                    <Box sx={styles.fieldBlock}>
                      <LatinTextField
                        fullWidth
                        variant="standard"
                        label="Street"
                        required
                        check={check}
                        name="street_name"
                        margin="dense"
                        errorFields={errorUnitFields?.street_name}
                        setErrorFields={setErrorUnitFields}
                        onChange={(e) =>
                          handleChangeItem(
                            'units_addresses',
                            'street_name',
                            index,
                            e
                          )
                        }
                        value={editedUnitDetails?.address?.street_name || ''}
                      />
                      <NumericTextField
                        fullWidth
                        variant="standard"
                        label="Number"
                        type="text"
                        name="number"
                        required
                        check={check}
                        errorFields={errorUnitFields?.number}
                        setErrorFields={setErrorUnitFields}
                        margin="dense"
                        minValue={1}
                        value={editedUnitDetails.address.number || ''}
                        onChange={(e) =>
                          handleChangeItem(
                            'units_addresses',
                            'number',
                            index,
                            e
                          )
                        }
                      />
                    </Box>
                    <Box sx={styles.fieldBlock}>
                      <TextField
                        fullWidth
                        variant="standard"
                        margin="dense"
                        label="Number add"
                        name="number_add"
                        onChange={(e) =>
                          handleChangeItem(
                            'units_addresses',
                            'number_add',
                            index,
                            e
                          )
                        }
                        value={editedUnitDetails.address.number_add || ''}
                      />
                      <TextField
                        fullWidth
                        variant="standard"
                        margin="dense"
                        label="Postcode"
                        onChange={(e) =>
                          handleChangeItem(
                            'units_addresses',
                            'postcode',
                            index,
                            e
                          )
                        }
                        value={editedUnitDetails.address.postcode || ''}
                      />
                    </Box>
                  </Box>
                  <Box sx={styles.editFieldsBlockHolder}>
                    <Box sx={styles.fieldBlock}>
                      <NumericTextField
                        fullWidth
                        variant="standard"
                        name="unit_size"
                        label="Size"
                        margin="dense"
                        onChange={(e) =>
                          handleChangeItem('units', 'size', index, e)
                        }
                        value={editedUnitDetails.size || ''}
                      />
                      <NumericTextField
                        fullWidth
                        variant="standard"
                        label="Built year"
                        // name="unit_built_year"
                        margin="dense"
                        name="built_year"
                        maxLength={4}
                        minValue={1200}
                        errorFields={errorUnitFields.built_year}
                        setErrorFields={setErrorUnitFields}
                        maxValue={new Date().getFullYear()}
                        onChange={(e) =>
                          handleChangeItem('units', 'built_year', index, e)
                        }
                        value={editedUnitDetails.built_year || ''}
                      />
                      <NumericTextField
                        fullWidth
                        variant="standard"
                        label="Price"
                        name="unit_price"
                        margin="dense"
                        onChange={(e) =>
                          handleChangeItem('units_price', 'price', index, e)
                        }
                        value={editedUnitDetails.price || ''}
                      />
                    </Box>
                    <Box sx={styles.fieldBlock}>
                      <PurposeAutocomplete
                        value={editedUnitDetails.purpose ?? ''}
                        options={options}
                        loading={loading}
                        freeSolo={true}
                        error={error}
                        label="Purpose"
                        variant="standard"
                        disabled={false}
                        onInputChange={(newInput) => {
                          // handleChangeItem("units", "purpose", index, newInput);
                          const q = newInput.trim();
                          if (q.length < 3) {
                            clear();
                            return;
                          }
                          if (q !== lastQuery) {
                            suggest(q);
                          }
                        }}
                        onValueChange={(selected) => {
                          handleChangeItem('units', 'purpose', index, selected);
                        }}
                        onInvalidInput={(message) => AlertService.warning(message)}
                      />
                      <TextField
                        fullWidth
                        variant="standard"
                        label="BUG ID"
                        margin="dense"
                        onChange={(e) =>
                          handleChangeItem('units', 'bug_id', index, e)
                        }
                        value={editedUnitDetails.bug_id || ''}
                        disabled
                      />
                      <TextField
                        fullWidth
                        variant="standard"
                        label="BUG URL"
                        margin="dense"
                        onChange={(e) =>
                          handleChangeItem('units', 'bug_url', index, e)
                        }
                        value={editedUnitDetails.bug_url || ''}
                        disabled
                      />
                    </Box>
                  </Box>
                  <Box sx={styles.editButtonGroup}>
                    <IconButton
                      type="button"
                      disabled={Object.values(errorUnitFields).includes(true)}
                      onClick={() => handleSetEditMode('edit', index)}
                    >
                      <DoneIcon fontSize={'small'} />
                    </IconButton>
                    <IconButton
                      type="button"
                      onMouseDown={handleCancelMouse}
                      onClick={() => handleSetEditMode('close', index)}
                    >
                      <CloseIcon fontSize={'small'} />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Box sx={styles.itemContainer} key={index}>
                  <Box sx={styles.itemHolderUnits}>
                    {/*<Box sx={styles.infoHolder}>*/}
                    {/*  <Box sx={styles.info}>*/}
                        <Typography sx={styles.itemDetails} variant={'h6'}>
                          Unit:
                        </Typography>
                        <Box sx={{minWidth: 0}}>
                          <ClipToolTip
                            title={getUnitName(item)}
                            sx={styles.itemDetails}
                          />
                        </Box>

                        {/*<Typography sx={styles.itemDetails} variant={'h6'}>*/}
                        {/*  {getUnitName(item)}*/}
                        {/*</Typography>*/}
                    {/*  </Box>*/}
                    {/*</Box>*/}
                    <Box sx={styles.iconHolder}>
                      <IconButton
                        type="button"
                        aria-label="Edit"
                        onMouseDown={handleCancelMouse}
                        onClick={() => handleSetEditMode('open', index)}
                      >
                        <EditIcon sx={styles.editIcon} />
                      </IconButton>
                      <IconButton
                        type="button"
                        aria-label="Delete"
                        onMouseDown={handleCancelMouse}
                        onClick={() => handleDeleteItem(index)}
                      >
                        <DeleteIcon sx={styles.editIcon} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              );
            }
          )}
      </AccordionDetails>
    </Accordion>
  );
});

export default ObjectUnitsBlock;
